import { useState, useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Alert from '@mui/material/Alert'
import IconButton from '@mui/material/IconButton'
import Collapse from '@mui/material/Collapse'
import Checkbox from '@mui/material/Checkbox'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import {
  listGroups, createGroup, deleteGroup,
  getGroup, addGroupMember, removeGroupMember,
  listRdMembers,
  type Group, type GroupMembership,
} from '../../services/authApi'

interface GroupsTabProps {
  rdId: string
}

export function GroupsTab({ rdId }: GroupsTabProps) {
  const [groups, setGroups] = useState<Group[]>([])
  const [error, setError] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null)
  const [groupMembers, setGroupMembers] = useState<GroupMembership[]>([])
  const [addMemberGroupId, setAddMemberGroupId] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setGroups(await listGroups(rdId))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load groups')
    }
  }, [rdId])

  useEffect(() => { load() }, [load])

  async function handleExpand(groupId: string) {
    if (expandedGroup === groupId) {
      setExpandedGroup(null)
      return
    }
    try {
      const data = await getGroup(rdId, groupId)
      setGroupMembers(data.members)
      setExpandedGroup(groupId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load group members')
    }
  }

  async function handleDelete(groupId: string) {
    if (!confirm('Delete this group? All memberships will be removed.')) return
    try {
      await deleteGroup(rdId, groupId)
      load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete group')
    }
  }

  async function handleRemoveMember(groupId: string, userId: string) {
    try {
      await removeGroupMember(rdId, groupId, userId)
      const data = await getGroup(rdId, groupId)
      setGroupMembers(data.members)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove member')
    }
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New Group
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ border: '1px solid', borderColor: 'divider' }} elevation={0}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Group</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map(g => (
              <>
                <TableRow key={g.id} hover>
                  <TableCell sx={{ width: 40 }}>
                    <IconButton size="small" onClick={() => handleExpand(g.id)}>
                      {expandedGroup === g.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell><strong>{g.name}</strong></TableCell>
                  <TableCell>{g.description || '—'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => setAddMemberGroupId(g.id)} title="Add member">
                      <PersonAddIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(g.id)} title="Delete group">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                <TableRow key={`${g.id}-expand`}>
                  <TableCell colSpan={4} sx={{ py: 0, borderBottom: expandedGroup === g.id ? undefined : 'none' }}>
                    <Collapse in={expandedGroup === g.id}>
                      <Box sx={{ py: 2, pl: 6 }}>
                        <Typography variant="overline">Members ({groupMembers.length})</Typography>
                        {groupMembers.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">No members</Typography>
                        ) : (
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                            {groupMembers.map(m => (
                              <Chip
                                key={m.userId}
                                label={m.userId.slice(0, 16) + '...'}
                                size="small"
                                onDelete={() => handleRemoveMember(g.id, m.userId)}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </>
            ))}
            {groups.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No groups
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CreateGroupDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); load() }}
        rdId={rdId}
      />

      <AddGroupMemberDialog
        open={!!addMemberGroupId}
        groupId={addMemberGroupId}
        rdId={rdId}
        existingGroupMembers={expandedGroup === addMemberGroupId ? groupMembers : []}
        onClose={() => setAddMemberGroupId(null)}
        onAdded={async () => {
          setAddMemberGroupId(null)
          if (expandedGroup) {
            const data = await getGroup(rdId, expandedGroup)
            setGroupMembers(data.members)
          }
        }}
      />
    </Box>
  )
}

function CreateGroupDialog({ open, onClose, onCreated, rdId }: {
  open: boolean; onClose: () => void; onCreated: () => void; rdId: string
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleCreate() {
    setError('')
    setLoading(true)
    try {
      await createGroup(rdId, { name, description })
      setName('')
      setDescription('')
      onCreated()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create group')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Group</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField fullWidth label="Group Name" value={name} onChange={e => setName(e.target.value)} sx={{ mt: 1, mb: 2 }} />
        <TextField fullWidth label="Description" value={description} onChange={e => setDescription(e.target.value)} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleCreate} disabled={loading || !name}>
          {loading ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function AddGroupMemberDialog({ open, groupId, rdId, existingGroupMembers, onClose, onAdded }: {
  open: boolean; groupId: string | null; rdId: string; existingGroupMembers: GroupMembership[]; onClose: () => void; onAdded: () => void
}) {
  const [rdMembers, setRdMembers] = useState<Array<{ userId: string; name: string; email: string }>>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    listRdMembers(rdId).then(members => {
      setRdMembers(members)
      setSelected(new Set())
    }).catch(() => setError('Failed to load RD members'))
  }, [open, rdId])

  const existingUserIds = new Set(existingGroupMembers.map(m => m.userId))
  const available = rdMembers.filter(m => !existingUserIds.has(m.userId))

  function toggleSelect(userId: string) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }

  function selectAll() {
    setSelected(new Set(available.map(m => m.userId)))
  }

  async function handleAdd() {
    if (!groupId || selected.size === 0) return
    setError('')
    setLoading(true)
    try {
      let added = 0
      for (const userId of selected) {
        try {
          await addGroupMember(rdId, groupId, { userId })
          added++
        } catch { /* skip duplicates */ }
      }
      if (added === 0) setError('No members were added')
      else onAdded()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add members')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Members to Group</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {available.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            All RD members are already in this group.
          </Typography>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {selected.size} of {available.length} selected
              </Typography>
              <Button size="small" onClick={selectAll}>Select All</Button>
            </Box>
            <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
              {available.map(m => (
                <Box
                  key={m.userId}
                  onClick={() => toggleSelect(m.userId)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 2, py: 1, cursor: 'pointer',
                    bgcolor: selected.has(m.userId) ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                    borderBottom: '1px solid', borderColor: 'divider',
                  }}
                >
                  <Checkbox size="small" checked={selected.has(m.userId)} />
                  <Box>
                    <Typography variant="body2">{m.name || m.email}</Typography>
                    {m.name && <Typography variant="caption" color="text.secondary">{m.email}</Typography>}
                  </Box>
                </Box>
              ))}
            </Paper>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleAdd} disabled={loading || selected.size === 0}>
          {loading ? 'Adding...' : `Add ${selected.size} Member${selected.size !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
